// src/pages/CheckPage.js
// Page « vérification » (ex. /verifier-dmarc-spf-dkim), contenu dans src/data/checkPages.js.
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, CheckCircle, AlertTriangle, XCircle, Info, ArrowRight, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import checkPages from '../data/checkPages';
import useSEO from '../hooks/useSEO';
import NotFound from './NotFound';

const STATUS = {
  ok: { Icon: CheckCircle, cls: 'text-green-400', label: 'OK' },
  warning: { Icon: AlertTriangle, cls: 'text-amber-400', label: 'À améliorer' },
  error: { Icon: XCircle, cls: 'text-red-400', label: 'Critique' },
  info: { Icon: Info, cls: 'text-zinc-400', label: 'Information' },
};

export default function CheckPage({ slug }) {
  const page = checkPages.find((p) => p.slug === slug);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);

  useSEO(`/${slug}`, {
    title: page?.seoTitle,
    description: page?.description,
  });

  if (!page) return <NotFound />;
  const start = () => navigate(user ? '/audits/new' : '/register');

  return (
    <div className="min-h-screen bg-[#09090b] text-white">
      {/* HEADER */}
      <header className="border-b border-zinc-800 bg-black/80">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <ShieldCheck className="w-7 h-7 text-blue-500" />
            <span className="font-mono text-lg font-black uppercase tracking-tighter">CyberGuard</span>
          </Link>
          <nav className="flex items-center gap-5">
            <Link to="/blog" className="hidden sm:inline text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-widest">Blog</Link>
            <Link to="/login" className="hidden sm:inline text-zinc-400 hover:text-white font-mono text-xs uppercase tracking-widest">Connexion</Link>
            <button onClick={start} className="bg-blue-600 hover:bg-blue-700 px-4 py-2 font-mono text-xs uppercase tracking-widest">
              Commencer
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-16">
        {/* HERO */}
        <p className="font-mono text-xs uppercase tracking-widest text-blue-400 mb-4">{page.kicker}</p>
        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-6">{page.h1}</h1>
        {page.intro.map((p, i) => (
          <p key={i} className="text-zinc-300 text-lg leading-relaxed mb-4">{p}</p>
        ))}
        <button onClick={start} className="mt-4 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 font-mono text-sm uppercase tracking-widest">
          {page.cta} <ArrowRight className="w-4 h-4" />
        </button>

        {/* EXPLICATIONS */}
        {page.sections.map((s, i) => (
          <section key={i} className="mt-20">
            <h2 className="text-2xl font-bold mb-6">{s.title}</h2>
            <dl className="space-y-4">
              {s.items.map((it) => (
                <div key={it.term} className="bg-zinc-900 border border-zinc-800 p-5">
                  <dt className="font-mono font-bold text-blue-400 mb-1">{it.term}</dt>
                  <dd className="text-zinc-300 leading-relaxed">{it.text}</dd>
                </div>
              ))}
            </dl>
            {s.note && <p className="text-zinc-400 mt-6 leading-relaxed">{s.note}</p>}
          </section>
        ))}

        {/* CE QUE L'OUTIL VÉRIFIE */}
        <section className="mt-20">
          <h2 className="text-2xl font-bold mb-6">{page.checksTitle}</h2>
          <ul className="space-y-3">
            {page.checks.map((c, i) => (
              <li key={i} className="flex gap-3 text-zinc-300 leading-relaxed">
                <CheckCircle className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                <span>{c}</span>
              </li>
            ))}
          </ul>
          <p className="text-zinc-500 text-sm mt-5 leading-relaxed">{page.checksNote}</p>
        </section>

        {/* EXEMPLE */}
        <section className="mt-20">
          <h2 className="text-2xl font-bold mb-6">{page.exampleTitle}</h2>
          <div className="bg-black border border-zinc-800 font-mono text-sm">
            <div className="border-b border-zinc-800 px-5 py-3 text-zinc-500">$ scan {page.exampleDomain}</div>
            <ul className="p-5 space-y-3">
              {page.example.map((e, i) => {
                const { Icon, cls, label } = STATUS[e.status];
                return (
                  <li key={i} className="flex gap-3">
                    <Icon className={`w-5 h-5 shrink-0 ${cls}`} aria-label={label} />
                    <span className="text-zinc-200">{e.text}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <p className="text-zinc-400 mt-5 leading-relaxed">{page.exampleNote}</p>
        </section>

        {/* CORRECTION */}
        <section className="mt-20">
          <h2 className="text-2xl font-bold mb-6">{page.fixTitle}</h2>
          <ol className="space-y-6">
            {page.fixSteps.map((st, i) => (
              <li key={i} className="flex gap-4">
                <span className="font-mono font-black text-blue-500 text-xl w-6 shrink-0">{i + 1}</span>
                <div>
                  <h3 className="font-bold mb-1">{st.title}</h3>
                  <p className="text-zinc-300 leading-relaxed">{st.text}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 border-l-2 border-amber-500 bg-amber-500/5 p-4 text-zinc-300 leading-relaxed">
            {page.fixWarning}
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-20">
          <h2 className="text-2xl font-bold mb-6">Questions fréquentes</h2>
          <div className="divide-y divide-zinc-800 border-y border-zinc-800">
            {page.faqs.map((f, i) => (
              <div key={i}>
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 py-4 text-left font-semibold"
                  aria-expanded={openFaq === i}
                >
                  {f.q}
                  <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && <p className="pb-4 text-zinc-300 leading-relaxed">{f.a}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* CTA FINAL */}
        <section className="mt-20 bg-zinc-900 border border-zinc-800 p-8 text-center">
          <h2 className="text-2xl font-bold mb-3">{page.ctaTitle || 'Votre domaine est-il protégé ?'}</h2>
          <p className="text-zinc-400 mb-6">Premier audit offert, sans carte bancaire. Résultat en moins de 2 minutes.</p>
          <button onClick={start} className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 px-6 py-3 font-mono text-sm uppercase tracking-widest">
            {page.cta} <ArrowRight className="w-4 h-4" />
          </button>
        </section>

        {/* AUTRES VÉRIFICATIONS */}
        <section className="mt-12">
          <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">Autres vérifications</h2>
          <ul className="flex flex-wrap gap-3">
            {checkPages.filter((c) => c.slug !== page.slug).map((c) => (
              <li key={c.slug}>
                <Link to={`/${c.slug}`} className="inline-block border border-zinc-800 hover:border-blue-500/50 px-4 py-2 text-sm text-zinc-300 hover:text-white">
                  {c.navLabel}
                </Link>
              </li>
            ))}
          </ul>
        </section>

        {page.related?.length > 0 && (
          <section className="mt-12">
            <h2 className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-3">À lire aussi</h2>
            <ul className="space-y-2">
              {page.related.map((r) => (
                <li key={r.href}><Link to={r.href} className="text-blue-400 hover:underline">{r.label}</Link></li>
              ))}
            </ul>
          </section>
        )}
      </main>

      <footer className="border-t border-zinc-800 py-8 px-4">
        <div className="max-w-5xl mx-auto flex flex-wrap gap-6 justify-center">
          <Link to="/" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">Accueil</Link>
          <Link to="/blog" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">Blog</Link>
          <Link to="/cgv" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">CGU</Link>
          <Link to="/mentions-legales" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">Mentions légales</Link>
          <Link to="/confidentialite" className="text-zinc-400 hover:text-white text-xs font-mono uppercase tracking-widest">Confidentialité</Link>
        </div>
      </footer>
    </div>
  );
}
